import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import LocationMap from './components/LocationMap';
import HoursSchedule from './components/HoursSchedule';
import ContactInfo from './components/ContactInfo';
import ParkingTransit from './components/ParkingTransit';
import NeighborhoodGuide from './components/NeighborhoodGuide';
import ContactForm from './components/ContactForm';
import SocialFeed from './components/SocialFeed';

const VisitConnect = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const quickActions = [
    {
      icon: "Phone",
      title: "Call Us",
      description: "Speak with our team",
      action: "tel:+14165550123",
      label: "(416) 555-0123"
    },
    {
      icon: "Navigation",
      title: "Get Directions",
      description: "Navigate to our bakery",
      action: "https://www.google.com/maps/dir/?api=1&destination=43.6532,-79.5831",
      label: "Open in Maps"
    },
    {
      icon: "ShoppingBag",
      title: "Order Online",
      description: "Browse our menu",
      action: "/menu-showcase",
      label: "View Menu"
    },
    {
      icon: "Calendar",
      title: "Book Catering",
      description: "Plan your event",
      action: "/catering-excellence",
      label: "Learn More"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Visit & Connect - Montesanto Bakery | Location, Hours & Contact</title>
        <meta 
          name="description" 
          content="Visit Montesanto Bakery in Etobicoke. Find our location, opening hours, parking information, and get in touch for custom orders and catering inquiries." 
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20">
          <section className="relative py-16 bg-gradient-to-br from-primary/5 via-background to-accent/5">
            <div className="container-custom">
              <div className="max-w-3xl mx-auto text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-6">
                  <Icon name="MapPin" size={16} />
                  <span>Visit Our Bakery</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
                  Come Visit Us in Etobicoke
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Experience the warmth of authentic Italian baking traditions. We're located in the heart of Etobicoke, ready to welcome you with fresh pastries and genuine hospitality.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                {quickActions?.map((action, index) => (
                  <a
                    key={index}
                    href={action?.action}
                    target={action?.action?.startsWith('http') ? '_blank' : undefined}
                    rel={action?.action?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="group p-6 rounded-2xl bg-card hover:bg-primary/5 border-2 border-border hover:border-primary/20 transition-all duration-300 text-center shadow-warm"
                  >
                    <div className="w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center mx-auto mb-4 transition-colors">
                      <Icon name={action?.icon} size={26} color="var(--color-primary)" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {action?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {action?.description}
                    </p>
                    <span className="text-sm font-medium text-primary group-hover:underline">
                      {action?.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 bg-background">
            <div className="container-custom">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <LocationMap />
                <HoursSchedule />
              </div>
            </div>
          </section>

          <section className="py-16 bg-muted/20">
            <div className="container-custom">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ContactInfo />
                <ParkingTransit />
              </div>
            </div>
          </section>

          <section className="py-16 bg-background">
            <div className="container-custom">
              <NeighborhoodGuide />
            </div>
          </section>

          <section className="py-16 bg-muted/20">
            <div className="container-custom">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <ContactForm />
                </div>
                <div>
                  <SocialFeed />
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Icon name="Heart" size={32} color="var(--color-primary)" />
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  We Can't Wait to Welcome You
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Whether you're stopping by for your morning cornetto, picking up a custom cake, or exploring our full menu of authentic Italian baked goods, we're here to make your visit special.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="tel:+14165550123">
                    <button className="px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-all duration-300 flex items-center gap-2">
                      <Icon name="Phone" size={20} />
                      Call Now
                    </button>
                  </a>
                  <a href="/menu-showcase">
                    <button className="px-6 py-3 rounded-lg bg-card text-foreground font-medium border-2 border-border hover:border-primary/20 hover:bg-primary/5 transition-all duration-300 flex items-center gap-2">
                      <Icon name="UtensilsCrossed" size={20} />
                      View Menu
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default VisitConnect;